export type AiScribeStep =
  | "initial"
  | "recordingInitializing"
  | "recordingActive"
  | "paused"
  | "finishing"
  | "finished"
  | "finishedRetry"
  | "processingRetry"
  | "cancelingRetry"
  | "finishedMerge"
  | "processingMerge"
  | "cancelingMerge"
  | "errorMicrophoneInitial"
  | "errorMicrophoneInterrupted"
  | "errorNoRetry"
  | "errorTimeoutProcessing"
  | "errorWhileProcessing"
  | "errorWithRetry"
  | "unsupportedNote";

/**
 * Language values used by Nabla's API.
 */
export const NablaAPILanguage = {
  English: "en-US",
  Spanish: "es-ES",
  Chinese: "zh-CN",
  Cantonese: "zh-HK",
  German: "de-DE",
  French: "fr-FR",
  Japanese: "ja-JP",
  Korean: "ko-KR",
  Russian: "ru-RU",
  Portuguese: "pt-BR",
  Tagalog: "tl-PH",
  Hindi: "hi-IN",
  Vietnamese: "vi-VN",
  Arabic: "ar-AE",
} as const;

export type NablaAPILanguage =
  (typeof NablaAPILanguage)[keyof typeof NablaAPILanguage];

export const NABLA_API_LANGUAGE_LABELS = {
  [NablaAPILanguage.English]: "English",
  [NablaAPILanguage.Spanish]: "Spanish",
  [NablaAPILanguage.Chinese]: "Chinese (Mandarin)",
  [NablaAPILanguage.Cantonese]: "Chinese (Cantonese)",
  [NablaAPILanguage.German]: "German",
  [NablaAPILanguage.French]: "French",
  [NablaAPILanguage.Japanese]: "Japanese",
  [NablaAPILanguage.Korean]: "Korean",
  [NablaAPILanguage.Russian]: "Russian",
  [NablaAPILanguage.Portuguese]: "Portuguese",
  [NablaAPILanguage.Tagalog]: "Tagalog",
  [NablaAPILanguage.Hindi]: "Hindi",
  [NablaAPILanguage.Vietnamese]: "Vietnamese",
  [NablaAPILanguage.Arabic]: "Arabic",
} satisfies Record<NablaAPILanguage, string>;

export const NABLA_API_LANGUAGE_OPTIONS = Object.values(NablaAPILanguage).map(
  (value) => ({
    value,
    label: NABLA_API_LANGUAGE_LABELS[value],
  }),
);

export function isNablaAPILanguage(value: unknown): value is NablaAPILanguage {
  return (
    typeof value === "string" &&
    Object.values(NablaAPILanguage).includes(value as NablaAPILanguage)
  );
}

export type NablaTranscriptItem = {
  end_offset_ms: number;
  id: string;
  is_final: boolean;
  object: "transcript_item";
  speaker: "doctor" | "patient" | "unspecified";
  start_offset_ms: number;
  text: string;
};

/**
 * A note transcript item according to hippo.
 */
export type NoteTranscriptItem = {
  text: string;
  source: string;
  speaker: string;
  is_final: boolean;
  start_offset_ms: number;
  end_offset_ms: number;
  state: "not_sent" | "sent" | "processed" | "errored";
};

/**
 * A client-side version of our `NoteTranscriptItem` model that also carries over
 * Nabla transcript items' `id` and `is_final` fields.
 */
export type ClientNoteTranscriptItem = NoteTranscriptItem &
  Pick<NablaTranscriptItem, "id" | "is_final">;
